import { noop } from 'lodash-es';

import {
  BufferConfig,
  PlayerRequestInterceptor,
  PlayerRequestType,
  composePlayerRequestInterceptors,
} from '@dtvgo/player-common-utils';
import {
  getCDNTokenizationConfig,
  getPlaybackInfoWithAuthorizerV3,
  hasTokenization,
} from '@dtvgo/player-utils';
import { ErrorCode, Source } from '@dtvgo/error';
import {
  buildTimeShiftedManifestUrl,
  stripQueryParams,
} from '@dtvgo/shaka-player-utils';

import { generateNonce } from '~/cast/lib';
import { middleware } from '~/services/middleware';

import { tokensAreUsable } from '~/store/credentials';
import { TokenType } from '~/utils/appConfig';
import { errorFactory } from '~/utils/errorFactory';
import { getCatchUpStart } from './getCatchUpStart';

type LoadRequestData = framework.messages.LoadRequestData;

function parseLoadRequest(request: LoadRequestData) {
  const {
    assetId: contentId,
    forgerockTokensData: { idToken = undefined } = {},
    selectedProfile: { id: profileId = undefined } = {},
    isLive = false,
    start,
  } = request.media.customData || {};

  if (!idToken || !profileId || !contentId) {
    throw errorFactory.create('Some required params are missing', {
      source: Source.GENERAL,
      code: ErrorCode.MISSING_DATA,
      data: { idToken, profileId, contentId },
    });
  }

  return {
    idToken,
    profileId,
    contentId,
    isLive,
    start,
  };
}

/**
 * Creates a request interceptor that appends the PAL nonce to manifest URLs.
 * The nonce is added as the `givn` query parameter for ad requests.
 */
function createNonceRequestInterceptor(
  nonce: string,
): PlayerRequestInterceptor {
  return (requestType, request) => {
    // Only add nonce to manifest requests
    if (requestType !== PlayerRequestType.MANIFEST || !request.uris?.[0]) {
      return request;
    }

    // If the request already has a `givn` parameter, we assume the nonce is already present and do not modify the URL
    if (new URL(request.uris[0]).searchParams.has('givn')) {
      return request;
    }

    const url = new URL(request.uris[0]);
    url.searchParams.set('givn', nonce);

    return {
      ...request,
      uris: [url.toString()],
    };
  };
}

/**
 * Builds the request interceptor chain, optionally including PAL nonce and CDN tokenization.
 */
function buildRequestInterceptors(
  contentUrl: string,
  nonce: string | undefined,
  cdnTokenizationTokenType: TokenType[],
) {
  let onRequest: PlayerRequestInterceptor | undefined;
  let onResponseReceived;

  if (hasTokenization({ manifestUrl: contentUrl })) {
    ({ onRequest, onResponseReceived } = getCDNTokenizationConfig(
      cdnTokenizationTokenType,
    ));
  }

  // Add PAL nonce to manifest requests if available
  if (nonce) {
    const nonceInterceptor = createNonceRequestInterceptor(nonce);
    onRequest =
      onRequest ?
        composePlayerRequestInterceptors(onRequest, nonceInterceptor)
      : nonceInterceptor;
  }

  return { onRequest, onResponseReceived };
}

export function getPlaybackInfo(
  options: {
    lowLatency: boolean;
    cdnTokenizationTokenType: TokenType[];
    buffer: {
      live: BufferConfig;
      vod: BufferConfig;
    };
  },
  events?: {
    onLoadRequest?: (request: {
      isLive: boolean;
      contentId: string;
      idToken: string;
      profileId: string;
      start?: number;
    }) => Promise<void>;
    onPlaybackInfoAvailable?: (
      content: { isLive: boolean; contentId: string },
      playbackInfo: Awaited<ReturnType<typeof getPlaybackInfoWithAuthorizerV3>>,
    ) => void;
    onNonceGenerated?: (nonce: string) => void;
    onAuthorizerError?: (error: Error) => void;
    onFinished?: () => void;
  },
) {
  return async (request: LoadRequestData) => {
    try {
      const { idToken, profileId, contentId, isLive, start } =
        parseLoadRequest(request);
      const catchUpStart = getCatchUpStart(isLive, contentId, start);

      // Generate PAL nonce for ad requests
      const nonce = await generateNonce(request, {
        ppid: profileId,
      });
      if (nonce && events?.onNonceGenerated) {
        events.onNonceGenerated(nonce);
      }

      if (events?.onLoadRequest) {
        await events.onLoadRequest({
          idToken,
          profileId,
          isLive,
          contentId,
          start: catchUpStart,
        });
      }

      const playbackInfo = await getPlaybackInfoWithAuthorizerV3(
        middleware,
        { isLive, contentId },
        {
          onAuthorizerError: events?.onAuthorizerError || noop,
          refreshSecondsBefore: 90,
        },
        tokensAreUsable,
      );

      const { provider, channelId, sources, clearRefreshTimeout } =
        playbackInfo;

      events?.onPlaybackInfoAvailable?.({ isLive, contentId }, playbackInfo);

      // TODO: Allow switch to other streams on failure
      const [source] =
        options.lowLatency ? sources : (
          sources.filter(({ isLowLatency }) => !isLowLatency)
        );

      // This logic means we prefer dash, but fallback to hls if dash is not available (P+ content)
      const sourceUrl = source?.dash || source?.hls;

      const onFinished = () => {
        clearRefreshTimeout();
        events?.onFinished?.();
      };

      if (!sourceUrl) {
        onFinished();
        throw errorFactory.create('No playable source found', {
          source: Source.GENERAL,
          code: ErrorCode.MISSING_DATA,
        });
      }

      const contentUrl =
        catchUpStart === undefined ? sourceUrl : (
          buildTimeShiftedManifestUrl(
            stripQueryParams(sourceUrl, ['ads.startOver']),
            { start: catchUpStart },
          )
        );

      const { widevine, playready } = source.keySystemsConfig || {};
      const drmConfig = widevine || playready;

      const { onRequest, onResponseReceived } = buildRequestInterceptors(
        contentUrl,
        nonce,
        options.cdnTokenizationTokenType,
      );

      return {
        contentId,
        contentUrl,
        catchUpStart,
        streamType:
          isLive ?
            cast.framework.messages.StreamType.LIVE
          : cast.framework.messages.StreamType.BUFFERED,
        drmConfig,
        bufferConfig: isLive ? options.buffer.live : options.buffer.vod,
        customData: {
          provider: provider || undefined,
          channelId,
          start: catchUpStart,
        },
        onRequest,
        onResponseReceived,
        onFinished,
      };
    } catch (error) {
      console.error('[cast>VrioOtt>getPlaybackInfo] error', error);
      const { ErrorData, ErrorReason, ErrorType } = cast.framework.messages;
      const errorData = new ErrorData(ErrorType.LOAD_FAILED);
      errorData.reason = ErrorReason.APP_ERROR;
      errorData.customData = { error: errorFactory.ensureVrioOttError(error) };
      return errorData;
    }
  };
}
